import { faEdit, faLightbulb, faPlus, faSlash, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import useModalManager from '@hooks/useModalManager';
import { Button } from '@ui';
import { cx } from 'class-variance-authority';
import { useClubSlug } from 'components/Clubs/hooks';
import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import AllocationGroupForm from '../AllocationGroupForm';
import { AllocationGroup } from '../types';

interface Props {
    name: string;
}

const AllocationGroupGridInput = ({ name }: Props) => {
    const { t } = useTranslation('space-manager');
    const clubSlug = useClubSlug();
    const { data, mutate } = useSWR<{ data: AllocationGroup[] }>(`club/${clubSlug}/space-manager/allocation-group`);
    const { control, watch } = useFormContext();
    const api = useApi();
    const {
        field: { value, onChange }
    } = useController({ control, name });
    const { addModal, removeModal } = useModalManager();
    const groupId = watch(name);

    useEffect(() => {
        if (data && data?.data?.findIndex((group) => group.id === groupId) === -1) onChange(undefined);
    }, [groupId, data]);

    const handleOpenAllocationGroupForm = (group?: AllocationGroup) => {
        addModal({
            title: t('allocation.group.create'),
            content: (id) => (
                <AllocationGroupForm
                    group={group}
                    callback={() => {
                        removeModal(id);
                        void mutate();
                    }}
                />
            )
        });
    };

    const handleSelect = (group: AllocationGroup) => {
        if (value == group.id) onChange(undefined);
        else onChange(group.id);
    };

    const handleDelete = useConfirmedCallback((group: AllocationGroup) => {
        void api({
            url: `space-manager/allocation-group/${group.id}`,
            method: 'DELETE',
            onSuccess: () => void mutate()
        });
    });

    return (
        <div className="tw-mt-2">
            <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-gap-4">
                {data?.data?.map((group) => (
                    <div
                        key={group.id}
                        className={cx(
                            group.id == value ? 'tw-selected' : 'tw-selectable',
                            'tw-card-flat tw-card-clickable tw-flex tw-flex-col tw-justify-between tw-gap-2 tw-px-4 tw-py-3'
                        )}
                        onClick={() => handleSelect(group)}
                    >
                        <div className="tw-mb-2 tw-flex tw-items-start tw-justify-between tw-gap-4">
                            <span className="tw-block tw-w-full tw-overflow-hidden tw-overflow-ellipsis tw-text-lg">
                                {group.name}
                            </span>
                            {/*<Checkbox disableAnimation isSelected={group.id == value} onChange={() => handleSelect(group)} />*/}
                            <div
                                className="tw-h-8 tw-w-8 tw-rounded"
                                style={{ backgroundColor: group.color ?? 'transparent' }}
                            />
                        </div>
                        <div className="tw-flex tw-justify-between tw-gap-4">
                            <span className="fa-layers fa-fw">
                                {group.disable_iot_output_switching && (
                                    <FontAwesomeIcon icon={faSlash} className="tw-text-accents-6" />
                                )}
                                <FontAwesomeIcon icon={faLightbulb} className="tw-text-accents-6" />
                            </span>

                            <div className="tw-flex tw-gap-2 tw-self-end">
                                <Button
                                    color="secondary"
                                    size="xs"
                                    auto
                                    onClick={() => handleOpenAllocationGroupForm(group)}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button color="secondary" size="xs" auto onClick={() => handleDelete(group)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Button
                color="secondary"
                size="xs"
                block
                auto
                onClick={() => handleOpenAllocationGroupForm()}
                icon={<FontAwesomeIcon icon={faPlus} />}
            >
                {t('allocation.group.create')}
            </Button>
        </div>
    );
};

export default AllocationGroupGridInput;
